import { RacemapAPIClient } from '@racemap/utilities/api-client';
import { OneSecondInMillis } from '@racemap/utilities/consts/time';
import { decodeBinary, type PointWithOffsetTimeNumber } from '@racemap/utilities/functions/codec';
import { median } from '@racemap/utilities/functions/utils';
import type { CollectedSample, JsonValue, TrackingIntervalStats } from './types';

const apiClient = RacemapAPIClient.fromWindowLocation();

export async function fetchRawPoints(
  eventId: string,
  starterId: string,
  beginMillis: number,
  endMillis: number,
): Promise<Array<PointWithOffsetTimeNumber>> {
  const range = `${new Date(beginMillis).toISOString()}--${new Date(endMillis).toISOString()}`;
  const rawBuffer = await apiClient.getEventPointsRaw(eventId, {
    starterIds: [starterId],
    range,
    offsetMode: 'none',
    clustered: false,
  });
  const trackMap = decodeBinary(rawBuffer);
  return trackMap.get(starterId) ?? [];
}

export function getValueByPath(root: unknown, path: string): JsonValue | undefined {
  const parts = path.split('.');
  let current: unknown = root;

  for (const part of parts) {
    if (current == null || typeof current !== 'object') {
      return undefined;
    }
    current = (current as Record<string, unknown>)[part];
  }

  return current as JsonValue | undefined;
}

// Derives the default y-axis index from a property path leaf name.
// Mirrors the Rust StarterCurrentContent field semantics:
//   0 = Speed   (m/s)  — speedSt, speed
//   1 = Distance (m)   — fromStart, toFinish, elv, gapDistance, distanceToShadowTrack
//   2 = Duration (s)   — eta, gap, gapChase, gapAverage
//   3 = Percentage (%) — progress or level
export function inferYAxisIndex(path: string): 0 | 1 | 2 | 3 {
  const leaf = path.split('.').pop() ?? '';
  if (leaf === 'speedSt' || leaf === 'speed') return 0;
  if (
    leaf === 'fromStart' ||
    leaf === 'toFinish' ||
    leaf === 'fromStartEx' ||
    leaf === 'toFinishEx' ||
    leaf === 'elv' ||
    leaf === 'gapDistance' ||
    leaf === 'gapDistanceEx' ||
    leaf === 'distanceToShadowTrack'
  )
    return 1;
  if (
    leaf === 'eta' ||
    leaf === 'gap' ||
    leaf === 'gapEx' ||
    leaf === 'gapChase' ||
    leaf === 'gapChaseEx' ||
    leaf === 'gapAverage' ||
    leaf === 'gapAverageEx'
  )
    return 2;

  if (leaf === 'battery') return 3;

  return 0;
}

// Returns only the last segment of a dot-separated path (e.g. "current.speedSt" → "speedSt").
export function pathLeaf(path: string): string {
  return path.split('.').pop() ?? path;
}

// Returns a map of path → value (as string) for every non-numeric path whose value
// does not change across all collected samples. Only populated when samples.length > 1.
export function classifyConstantProps(
  samples: Array<CollectedSample>,
  paths: ReadonlyArray<string>,
): Record<string, string> {
  if (samples.length < 2) return {};
  const result: Record<string, string> = {};
  for (const path of paths) {
    const firstRaw = samples[0].values[path];
    if (typeof firstRaw === 'number') continue;
    if (firstRaw == null) {
      // All null/undefined → constant with no real value; classify so it stays out of "changing".
      if (samples.every((s) => s.values[path] == null)) {
        result[path] = '—';
      }
      continue;
    }
    const firstStr = String(firstRaw);
    const isConstant = samples.every((s) => {
      const v = s.values[path];
      return v == null ? false : String(v) === firstStr;
    });
    if (isConstant) result[path] = firstStr;
  }
  return result;
}

export function analyseTrackingIntervals(
  track: Array<PointWithOffsetTimeNumber>,
): TrackingIntervalStats {
  const nullResult = {
    minIntervalInMs: null,
    maxIntervalInMs: null,
    avgIntervalInMs: null,
    medianIntervalInMs: null,
    geomMedianIntervalInMs: null,
    modeIntervalInMs: null,
  };

  if (track.length < 2) return nullResult;

  const sorted = [...track].sort((a, b) => a.time - b.time);

  let minVal = Number.POSITIVE_INFINITY;
  let maxVal = Number.NEGATIVE_INFINITY;
  let sum = 0;
  let sumLog = 0;
  const counts = new Map<number, number>();
  const intervals: Array<number> = [];

  for (let i = 1; i < sorted.length; i++) {
    const interval = sorted[i].time - sorted[i - 1].time;
    if (interval <= 0) continue;
    intervals.push(interval);
    if (interval < minVal) minVal = interval;
    if (interval > maxVal) maxVal = interval;
    sum += interval;
    sumLog += Math.log(interval);
    const roundedSeconds = Math.round(interval / OneSecondInMillis);
    counts.set(roundedSeconds, (counts.get(roundedSeconds) ?? 0) + 1);
  }

  if (intervals.length === 0) return nullResult;

  const n = intervals.length;

  let modeVal = 0;
  let modeCount = 0;
  for (const [val, count] of counts) {
    if (count > modeCount) {
      modeCount = count;
      modeVal = val;
    }
  }

  return {
    minIntervalInMs: minVal,
    maxIntervalInMs: maxVal,
    avgIntervalInMs: sum / n,
    medianIntervalInMs: median(intervals),
    geomMedianIntervalInMs: Math.exp(sumLog / n),
    modeIntervalInMs: modeVal * OneSecondInMillis,
  };
}

export const formatHHMMSS = (ms: number) => {
  const d = new Date(ms);
  return `${d.getUTCHours().toString().padStart(2, '0')}:${d.getUTCMinutes().toString().padStart(2, '0')}:${d.getUTCSeconds().toString().padStart(2, '0')}`;
};

export const formatDurationHHMMSS = (seconds: number) => {
  const sign = seconds < 0 ? '-' : '';
  const d = new Date(Math.abs(seconds) * OneSecondInMillis);
  return `${sign}${d.getUTCHours().toString().padStart(2, '0')}:${d.getUTCMinutes().toString().padStart(2, '0')}:${d.getUTCSeconds().toString().padStart(2, '0')}`;
};
